import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable, tap} from 'rxjs';
import {environment} from "../../../../environments/environment";
import {ZonesService} from "../../../shared/services/zones.service";

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatRequest {
  prompt: string;
  zoneId: string;
  zoneName: string;
  municipalityId: string;
}

export interface ChatResponse {
  data: string;
  fails: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AiService {
  private apiUrl = `${environment.services_api_url}${environment.v1}/ai/chat`;
  private readonly STORAGE_KEY = 'chat_history';
  private chatHistorySubject = new BehaviorSubject<ChatMessage[]>(this.loadChatHistoryFromStorage());

  // Observable for components to subscribe to
  private chatHistory$ = this.chatHistorySubject.asObservable();

  constructor(private http: HttpClient, private zoneService: ZonesService) {
  }

  // Load chat history from localStorage
  private loadChatHistoryFromStorage(): ChatMessage[] {
    const storedHistory = localStorage.getItem(this.STORAGE_KEY);
    return storedHistory ? JSON.parse(storedHistory) : [];
  }

  // Save chat history to localStorage
  private saveChatHistoryToStorage(history: ChatMessage[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
    this.chatHistorySubject.next(history);
  }

  // Get current chat history
  get chatHistory(): ChatMessage[] {
    return this.loadChatHistoryFromStorage();
  }

  // Clear chat history
  clearChatHistory(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.chatHistorySubject.next([]);
  }

  sendChatMessage(prompt: string): Observable<ChatResponse> {
    // Add user message to history
    const userMessage: ChatMessage = {role: 'user', content: prompt};
    const updatedHistory = [...this.chatHistory, userMessage];
    this.saveChatHistoryToStorage(updatedHistory);

    // Format history into a string
    const historyString = this.chatHistory
      .map(msg => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
      .join('\n\n');

    // Combine history with new prompt
    // Prepare request with combined prompt
    const request: ChatRequest = {
      prompt: historyString,
      zoneName: this.zoneService.getZonesValuesStorage().zone_name.toString(),
      zoneId: this.zoneService.getZonesValuesStorage().zone.toString(),
      municipalityId: this.zoneService.getZonesValuesStorage().municipality.toString()
    };

    // Send request and handle response
    return this.http.post<ChatResponse>(this.apiUrl, request).pipe(
      tap(response => {
        if (!response.fails) {
          // Add assistant response to history
          const assistantMessage: ChatMessage = {
            role: 'assistant',
            content: response.data
          };
          this.saveChatHistoryToStorage([...this.chatHistory, assistantMessage]);
        }
      })
    );
  }
}
